import axios, { AxiosError } from 'axios';
import { logger } from '@parrot-software/observability';
import { getAuthToken } from 'utils/auth';
import { extractErrorAttributes } from '@subscriptions/application/utils';
import { createApiError } from '@subscriptions/application/errors';

const subLog = logger.forDomain('SUB');

const authTokens = getAuthToken();
const authorization = authTokens?.accessToken ? { authorization: `Bearer ${authTokens.accessToken}` } : undefined;
const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'Accept-Language': 'es-MX',
  ...authorization,
};

/** Axios instance bound to the legacy Parrot backend (REACT_APP_BASE_URL_API) */
export const parrotApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_API,
  headers,
});

/** Axios instance bound to the parrot-subscriptions-api (REACT_APP_BASE_URL_SUBSCRIPTIONS_API) */
export const subscriptionsApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_SUBSCRIPTIONS_API,
  headers,
});

/** Axios instance bound to parrot-onboarding-api (REACT_APP_BASE_URL_ONBOARDING_API) */
export const onboardingApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_ONBOARDING_API,
  headers: { Accept: 'application/json', 'Accept-Language': 'es-MX' },
});

/**
 * Boundary breadcrumb for every HTTP failure reaching parrot-onboarding-api.
 * Re-throws the original AxiosError on purpose: the CSF upload step tells 5xx from 4xx
 * by reading `response.status`, so wrapping it here would break `isServerError`.
 */
onboardingApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    subLog.warn('handleApiError', extractErrorAttributes(error));
    return Promise.reject(error);
  },
);

type ApiErrorBody = {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
};

/**
 * Boundary breadcrumb for every HTTP failure reaching the subscriptions API.
 * Emitted as `warn` so it does not trigger Datadog Error Tracking — the use
 * case that catches the rejection is responsible for the final severity
 * (`error` for technical failures, `warn` for expected business outcomes).
 */
subscriptionsApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorBody>) => {
    const apiError = error.response?.data?.error;
    const status = error.response?.status ?? 0;

    if (apiError) {
      const wrapped = createApiError({
        code: apiError.code,
        message: apiError.message,
        status,
        details: apiError.details,
      });
      subLog.warn('handleApiError', {
        ...extractErrorAttributes(wrapped),
        'apiError.code': apiError.code,
      });
      return Promise.reject(wrapped);
    }

    subLog.warn('handleApiError', extractErrorAttributes(error));
    return Promise.reject(error);
  },
);
